import axios from 'axios';
import { Capacitor } from '@capacitor/core';
import i18n from '../i18n';

const BASE = process.env.REACT_APP_BACKEND_URL + '/api';
// SESSION_HINT_KEY is a non-sensitive flag used to know whether the current
// browser has a live server session (an httpOnly `access_token` cookie). We
// can't read the cookie itself from JS by design — this is just a marker set
// on login/register and cleared on logout so pages like SharedFolder can show
// "sign in" vs "linked to my account" without a network round-trip.
const SESSION_HINT_KEY = 'tofbox_session_hint';

// One-shot cleanup of the legacy localStorage token (iter 93 migration).
// If a returning user still has `tofbox_token` sitting in localStorage from
// before the httpOnly-cookie migration, it's dead weight and confuses anyone
// inspecting the browser storage. We remove it silently on module load.
try { localStorage.removeItem('tofbox_token'); } catch { /* ignore */ }

// In-memory fallback token. Populated from the login/register/google response
// body (backend still returns access_token there for backward compat). We use
// it as a Bearer header alongside the cookie so the app still works if the
// browser rejects the httpOnly cookie for any reason:
//   - iOS Safari installed PWA blocking SameSite=Lax cross-context requests
//   - Strict third-party-cookie policies (Firefox ETP, Brave)
//   - Users with cookies disabled
// Kept in a module variable — never in localStorage/sessionStorage — so it
// vanishes on tab close and can't be XSS-scraped from persistent storage.
let _memoryToken = null;

// `withCredentials: true` makes axios attach the httpOnly `access_token`
// cookie to every request. This is the primary auth transport.
//
// `timeout: 20000` — without this, a stalled connection (switching between
// WiFi/mobile data, a carrier-level blackhole, etc.) leaves the request
// pending forever: no response, no rejection, nothing for a catch block to
// surface as an error. On login that reads as "the button doesn't react" —
// the spinner just never stops. 20s comfortably covers the slowest
// legitimate call (item save, capped at 12s for its own metadata fetch)
// while still guaranteeing every request eventually fails loud instead of
// hanging silent.
const api = axios.create({ baseURL: BASE, withCredentials: true, timeout: 20000 });

export { api };

// Runs `call` on successive slices of `ids` and returns the last response.
const inBatches = async (ids, call, size = 500) => {
  let res = await call(ids.slice(0, size));
  for (let i = size; i < ids.length; i += size) res = await call(ids.slice(i, i + size));
  return res;
};

// Belt-and-suspenders: also attach Bearer if we have an in-memory token.
// Backend `get_current_user_from_header` reads the cookie FIRST, so this
// header is silently ignored when the cookie is present. It only kicks in
// when the browser dropped the cookie.
api.interceptors.request.use((config) => {
  if (_memoryToken) {
    config.headers = config.headers || {};
    config.headers.Authorization = `Bearer ${_memoryToken}`;
  }
  return config;
});

// No API route returns an HTML page. If one comes back anyway, something
// between us and the backend answered instead of it (e.g. a SPA fallback
// serving index.html) — fail loudly rather than hand callers a string where
// they expect an object, which silently reads as `undefined` everywhere.
const isHtmlDocument = (data) => typeof data === 'string' && /^\s*<(!doctype html|html)/i.test(data);

// On 401 (expired/invalid session), clear the hint and redirect to login.
api.interceptors.response.use(
  (r) => {
    if (isHtmlDocument(r.data)) {
      return Promise.reject(new Error(`API returned an HTML page instead of JSON: ${r.config?.url}`));
    }
    return r;
  },
  (err) => {
    if (err.response?.status === 429 && err.response.data) {
      // Rate limit (utils.rate_limit): show it in the user's language.
      err.response.data.detail = i18n.t('errors.tooManyAttempts');
    }
    const isAuthEndpoint = err.config?.url?.includes('/auth/');
    const onAuthPage = ['/login', '/register', '/forgot-password', '/u/', '/s/', '/f/'].some(
      p => window.location.pathname.startsWith(p)
    );
    if (err.response?.status === 401 && !isAuthEndpoint && !onAuthPage) {
      try { localStorage.removeItem(SESSION_HINT_KEY); } catch { /* ignore */ }
      clearMemoryToken();
      // Preserve the current path (+ hash, e.g. `#notifications`) so the
      // login flow can bring the user back exactly where they intended.
      const target = `${window.location.pathname}${window.location.search || ''}${window.location.hash || ''}`;
      const next = target && target !== '/' ? `?next=${encodeURIComponent(target)}` : '';
      window.location.href = `/login${next}`;
    }
    return Promise.reject(err);
  }
);

// -- Legacy shims kept for backwards compatibility ----------------------------
// `setToken` / `getToken` used to read/write the JWT in localStorage. That
// storage is now unused: the server sets an httpOnly cookie which JS cannot
// read (that's the point — protects against XSS). We keep these functions as
// zero-cost no-ops so any older call site doesn't crash while it's migrated.
export const setToken = (_ignored) => {
  // Kept for compat — the httpOnly cookie is now the source of truth.
};

export const getToken = () => null; // no longer available client-side

// -- Session hint helpers -----------------------------------------------------
export const markSession = (on) => {
  try {
    if (on) localStorage.setItem(SESSION_HINT_KEY, '1');
    else localStorage.removeItem(SESSION_HINT_KEY);
  } catch { /* ignore quota / private-mode failures */ }
};
export const hasSessionHint = () => {
  try { return !!localStorage.getItem(SESSION_HINT_KEY); }
  catch { return false; }
};

// Called by AuthContext right after login/register/google. On the web the
// token lives in memory only (never on disk) as a Bearer fallback for a
// dropped cookie. The native app is different: its WebView origin
// (https://localhost) is cross-site to the API, so the SameSite=Lax session
// cookie is never sent and Bearer is the ONLY auth transport — kept in
// memory alone, every app restart would log the user out. There it's also
// persisted in the app's private, non-backed-up (allowBackup=false) storage.
const NATIVE_TOKEN_KEY = 'tofbox_native_token';
export const setMemoryToken = (token) => {
  _memoryToken = token || null;
  if (!Capacitor.isNativePlatform()) return;
  try {
    if (_memoryToken) localStorage.setItem(NATIVE_TOKEN_KEY, _memoryToken);
    else localStorage.removeItem(NATIVE_TOKEN_KEY);
  } catch { /* ignore */ }
};
export const clearMemoryToken = () => setMemoryToken(null);
if (Capacitor.isNativePlatform()) {
  try { _memoryToken = localStorage.getItem(NATIVE_TOKEN_KEY) || null; } catch { /* ignore */ }
}

export const authApi = {
  register: (d) => api.post('/auth/register', d),
  login: (e, p) => api.post('/auth/login', { email: e, password: p }),
  logout: () => api.post('/auth/logout'),
  me: () => api.get('/auth/me'),
  // The app's language goes along so a brand-new account gets its emails
  // in it, as with /register.
  google: (session_id, referral_code) => api.post('/auth/google', {
    session_id, referral_code: referral_code || undefined, language: (i18n.language || 'fr').split('-')[0],
  }),
  // `_v` cache-busts this GET — same reasoning as forcePwaPurge()'s URL
  // param: Android WebView's HTTP cache has repeatedly proven to cache
  // things it shouldn't in this app. A per-call-unique query string stops
  // it from ever serving a stale "taken/available" verdict for a
  // previously-checked username.
  checkUsername: (u) => api.get(`/auth/check-username/${u}`, { params: { _v: Date.now() } }),
  forgotPassword: (e) => api.post('/auth/forgot-password', { email: e }),
  resetPassword: (token, pw) => api.post('/auth/reset-password', { token, new_password: pw }),
  updateProfile: (d) => api.put('/auth/profile', d),
  acceptCharter: () => api.post('/auth/accept-charter'),
  deleteAccount: (password, confirm) => api.delete('/auth/account', { data: { password, confirm } }),
  exportData: (format = 'json') => api.get('/auth/export-data', {
    params: { format },
    responseType: 'blob',
  }),
};

export const vaultApi = {
  status: () => api.get('/vault/status'),
  setup: (payload) => api.post('/vault/setup', payload),
  encryptItem: (itemId, payload) => api.put(`/vault/items/${itemId}/encrypt`, payload),
  decryptItem: (itemId, payload) => api.put(`/vault/items/${itemId}/decrypt`, payload),
  listItems: () => api.get('/vault/items'),
  reencryptItem: (itemId, payload) => api.put(`/vault/items/${itemId}/reencrypt`, payload),
  enableRecovery: (payload) => api.post('/vault/recovery/enable', payload),
  recoveryBlob: () => api.get('/vault/recovery/blob'),
  disableRecovery: () => api.delete('/vault/recovery'),
  resetRequest: () => api.post('/vault/reset/request'),
  resetConfirm: (code) => api.post('/vault/reset/confirm', { code }),
  createTransfer: (payload) => api.post('/vault/transfer/create', payload),
  consumeTransfer: (sessionId) => api.get(`/vault/transfer/${sessionId}`),
  cancelTransfer: (sessionId) => api.delete(`/vault/transfer/${sessionId}`),
};

export const reportsApi = {
  submit: (payload) => api.post('/reports', payload),
};

// A folder or link someone shared, kept in my Tof-Box (a live view of their
// share, nothing copied — see backend/received_routes.py).
export const receivedApi = {
  receive: (kind, token) => api.post(`/received/${kind}/${encodeURIComponent(token)}`),
  list: () => api.get('/received'),
  remove: (id) => api.delete(`/received/${id}`),
};

export const categoriesApi = {
  getAll: () => api.get('/categories'),
  create: (d) => api.post('/categories', d),
  update: (id, d) => api.put(`/categories/${id}`, d),
  delete: (id) => api.delete(`/categories/${id}`),
  lock: (id, pin) => api.post(`/categories/${id}/lock`, { pin }),
  unlock: (id, pin) => api.post(`/categories/${id}/unlock`, { pin }),
  share: (id, opts) => api.post(`/categories/${id}/share`, opts || { expires_in: 'never' }),
  revokeShare: (id) => api.delete(`/categories/${id}/share`),
  getShared: (token, viewerToken) => api.get(`/categories/shared/${token}`, { params: viewerToken ? { viewer_token: viewerToken } : {} }),
  reserveSharedItem: (token, itemId, viewerToken, name) =>
    api.post(`/categories/shared/${token}/items/${itemId}/reserve`, { viewer_token: viewerToken, name }),
  unreserveSharedItem: (token, itemId, viewerToken) =>
    api.post(`/categories/shared/${token}/items/${itemId}/unreserve`, { viewer_token: viewerToken }),
  giftSeen: (id) => api.post(`/categories/${id}/gift-reservations/seen`),
};

export const itemsApi = {
  getAll: (opts) => api.get('/items', { params: opts }),
  search: (q) => api.get('/items/search', { params: { q } }),
  // Smart collections (backend/smart_collections.py): tile counts, one
  // collection's items, and their two actions.
  smartCounts: () => api.get('/items/smart'),
  smartItems: (id) => api.get(`/items/smart/${id}`),
  keepDuplicate: (itemId) => api.post('/items/smart/duplicates/keep', { item_id: itemId }),
  keepFromDust: (itemIds, days = 30) => api.post('/items/smart/dust/keep', { item_ids: itemIds, days }),
  getByCategory: (cid) => api.get(`/items?category_id=${cid}`),
  getFavorites: () => api.get('/items/favorites'),
  getCount: () => api.get('/items/count'),
  getStats: () => api.get('/items/stats'),
  getQuota: () => api.get('/items/quota'),
  create: (d) => api.post('/items', d),
  update: (id, d) => api.put(`/items/${id}`, d),
  delete: (id) => api.delete(`/items/${id}`),
  // The server takes 500 ids per call; bigger selections (an imported
  // Inbox, "select all") go through in batches.
  bulkDelete: (itemIds) => inBatches(itemIds, (ids) => api.post('/items/bulk-delete', { item_ids: ids })),
  bulkMove: (itemIds, categoryId) => inBatches(itemIds, (ids) => api.post('/items/bulk-move', { item_ids: ids, category_id: categoryId })),
  // Pocket / Raindrop / Instapaper / browser bookmarks export → Inbox.
  importLinks: (file) => {
    const form = new FormData();
    form.append('file', file);
    return api.post('/items/import', form, { timeout: 120000 });
  },
  toggleFavorite: (id) => api.post(`/items/${id}/favorite`),
  toggleSurveyCandidate: (id) => api.post(`/items/${id}/survey-candidate`),
  listSurveyCandidates: () => api.get('/items?is_survey_candidate=true'),
  togglePin: (id) => api.post(`/items/${id}/pin`),
  lock: (id, pin, currentPin) => api.post(`/items/${id}/lock`, { pin, current_pin: currentPin }),
  unlock: (id, pin, remove = false) => api.post(`/items/${id}/unlock`, { pin, remove }),
  share: (id, opts) => api.post(`/items/${id}/share`, opts),
  namedShare:      (id, d)   => api.post(`/items/${id}/named-share`, d),
  listShares:      (id)      => api.get(`/items/${id}/shares`),
  listShareRecipients: ()    => api.get('/items/shares/recipients'),
  revokeShare:     (shareId) => api.delete(`/items/shares/${shareId}`),
  fetchMetadata: (url) => api.post('/items/metadata-fetch', { url }),
  getShared: (token) => api.get(`/items/shared/${token}`),
  findSimilar: (id) => api.get(`/items/${id}/similar`),
  findCheaper: (id) => api.get(`/items/${id}/find-cheaper`),
  updatePhoto: (id, photo) => api.post(`/items/${id}/photo`, { photo }),
  getPublicProfile: (username) => api.get(`/items/public-profile/${username}`),
  createBusinessCard: (d) => api.post('/items/business-card', d),
  updateBusinessCard: (id, d) => api.put(`/items/${id}/business-card`, d),
  triageInbox: () => api.post('/items/inbox/triage'),
  applyTriage: (moves) => api.post('/items/inbox/triage/apply', { moves }),
  trackView: (id) => api.post(`/items/${id}/view`),
  getTrash: () => api.get('/items/trash'),
  restore: (id) => api.post(`/items/${id}/restore`),
  purge: (id) => api.delete(`/items/${id}/purge`),
  emptyTrash: () => api.delete('/items/trash/all'),
  setMemory: (id, memory_text) => api.patch(`/items/${id}/memory`, { memory_text }),
  getRandomMemory: () => api.get('/items/memory/random'),
};

export const sessionsApi = {
  list: () => api.get('/sessions'),
  revoke: (jti) => api.delete(`/sessions/${jti}`),
  revokeOthers: () => api.post('/sessions/revoke-others'),
};

export const familyApi = {
  status:        ()          => api.get('/family/status'),
  invite:        (email)     => api.post('/family/invite', email ? { email } : {}),  // no email = open link
  cancelInvite:  (token)     => api.delete(`/family/invites/${token}`),
  removeMember:  (userId)    => api.delete(`/family/members/${userId}`),
  leave:         ()          => api.post('/family/leave'),
  peekInvite:    (token)     => api.get(`/family/invite/${token}`),
  acceptInvite:  (token)     => api.post(`/family/invite/${token}/accept`),
};

export const surveysApi = {
  list:        ()             => api.get('/surveys'),
  folders:     ()             => api.get('/surveys/folders'),
  create:      (d)            => api.post('/surveys', d),
  get:         (id)           => api.get(`/surveys/${id}`),
  update:      (id, d)        => api.patch(`/surveys/${id}`, d),
  close:       (id)           => api.post(`/surveys/${id}/close`),
  remove:      (id)           => api.delete(`/surveys/${id}`),
  addOption:   (id, d)        => api.post(`/surveys/${id}/options`, d),
  removeOption:(id, optId)    => api.delete(`/surveys/${id}/options/${optId}`),
  exportCsv:   (id)           => api.get(`/surveys/${id}/export.csv`, { responseType: 'blob' }),
  // Public — no auth
  publicGet:   (token)        => api.get(`/public/surveys/${token}`),
  publicVote:  (token, d, voterCookie) => api.post(`/public/surveys/${token}/vote`, d, {
    headers: { 'X-Voter-Cookie': voterCookie },
  }),
};

export const secretSantaApi = {
  list:            ()                 => api.get('/secret-santa/events'),
  create:          (d)                => api.post('/secret-santa/events', d),
  get:             (id)               => api.get(`/secret-santa/events/${id}`),
  update:          (id, d)            => api.patch(`/secret-santa/events/${id}`, d),
  addExclusion:    (id, d)            => api.post(`/secret-santa/events/${id}/exclusions`, d),
  removeExclusion: (id, index)        => api.delete(`/secret-santa/events/${id}/exclusions/${index}`),
  draw:            (id)               => api.post(`/secret-santa/events/${id}/draw`),
  remove:          (id)               => api.delete(`/secret-santa/events/${id}`),
  // Public — no auth
  publicGetGroup:  (token)            => api.get(`/public/secret-santa/${token}`),
  publicJoin:      (token, d)         => api.post(`/public/secret-santa/${token}/join`, d),
  publicRecover:   (token, d)         => api.post(`/public/secret-santa/${token}/recover`, d),
  publicAddGiftIdea: (token, pid, d, revealToken) => api.post(
    `/public/secret-santa/${token}/participants/${pid}/gift-ideas`, d,
    { headers: { 'X-Reveal-Token': revealToken } },
  ),
  publicRemoveGiftIdea: (token, pid, ideaId, revealToken) => api.delete(
    `/public/secret-santa/${token}/participants/${pid}/gift-ideas/${ideaId}`,
    { headers: { 'X-Reveal-Token': revealToken } },
  ),
  publicReveal:    (revealToken)      => api.get(`/public/secret-santa/reveal/${revealToken}`),
};

export const projectsApi = {
  list: () => api.get('/projects'),
  create: (d) => api.post('/projects', d),
  get: (id) => api.get(`/projects/${id}`),
  update: (id, d) => api.patch(`/projects/${id}`, d),
  remove: (id) => api.delete(`/projects/${id}`),
  addItem: (id, item_id, section_id = null) => api.post(`/projects/${id}/items`, { item_id, section_id }),
  removeItemRef: (id, ref_id) => api.delete(`/projects/${id}/items/${ref_id}`),
  createSection: (id, name) => api.post(`/projects/${id}/sections`, { name }),
  renameSection: (id, sid, name) => api.patch(`/projects/${id}/sections/${sid}`, { name }),
  removeSection: (id, sid) => api.delete(`/projects/${id}/sections/${sid}`),
  reorder: (id, payload) => api.patch(`/projects/${id}/reorder`, payload),
  moveItemSection: (id, ref_id, section_id) => api.patch(`/projects/${id}/items/${ref_id}/section`, null, { params: { section_id } }),
  // Phase B — Collaboration
  invite: (id, email, role) => api.post(`/projects/${id}/invites`, { email, role }),
  listInvites: (id) => api.get(`/projects/${id}/invites`),
  revokeInvite: (id, inviteId) => api.delete(`/projects/${id}/invites/${inviteId}`),
  previewInvitation: (token) => api.get(`/projects/invitations/${token}`),
  acceptInvitation: (token) => api.post(`/projects/invitations/${token}/accept`),
  listMembers: (id) => api.get(`/projects/${id}/members`),
  updateMemberRole: (id, userId, role) => api.patch(`/projects/${id}/members/${userId}`, { role }),
  removeMember: (id, userId) => api.delete(`/projects/${id}/members/${userId}`),
  listActivity: (id) => api.get(`/projects/${id}/activity`),
  // Phase C — Budget & Export
  setItemMeta: (id, refId, payload) => api.patch(`/projects/${id}/items/${refId}/meta`, payload),
  setBudget: (id, payload) => api.patch(`/projects/${id}/budget`, payload),
  getBudget: (id) => api.get(`/projects/${id}/budget`),
  // Planned expenses: lines typed in the budget, and the links' amounts.
  addBudgetLine: (id, payload) => api.post(`/projects/${id}/budget/lines`, payload),
  updateBudgetLine: (id, lineId, payload) => api.patch(`/projects/${id}/budget/lines/${lineId}`, payload),
  deleteBudgetLine: (id, lineId) => api.delete(`/projects/${id}/budget/lines/${lineId}`),
  updateBudgetItem: (id, refId, payload) => api.patch(`/projects/${id}/budget/items/${refId}`, payload),
  // The project's one shared account (made the first time).
  budgetAccount: (id) => api.post(`/projects/${id}/budget/account`),
  exportPdfUrl: (id) => `${BASE}/projects/${id}/export.pdf`,
  // Presence + cross-project drag
  pingPresence: (id) => api.post(`/projects/${id}/presence/ping`),
  listPresence: (id) => api.get(`/projects/${id}/presence`),
  moveItemToProject: (id, refId, target_project_id, target_section_id = null) =>
    api.post(`/projects/${id}/items/${refId}/move`, { target_project_id, target_section_id }),
};

export const hydrationApi = {
  getSettings: () => api.get('/hydration/settings'),
  saveSettings: (payload) => api.put('/hydration/settings', payload),
  addLog: (amount_ml, source = 'manual') => api.post('/hydration/logs', { amount_ml, source }),
  deleteLog: (id) => api.delete(`/hydration/logs/${id}`),
  today: () => api.get('/hydration/today'),
  stats: () => api.get('/hydration/stats'),
  streak: () => api.get('/hydration/streak'),
  // PHASE B
  badges: () => api.get('/hydration/badges'),
  insights: () => api.get('/hydration/insights'),
};

export const sharedAccountsApi = {
  list: (projectId) => api.get(`/projects/${projectId}/shared-accounts`),
  create: (projectId, payload) => api.post(`/projects/${projectId}/shared-accounts`, payload),
  update: (accountId, payload) => api.patch(`/shared-accounts/${accountId}`, payload),
  remove: (accountId) => api.delete(`/shared-accounts/${accountId}`),
  listExpenses: (accountId) => api.get(`/shared-accounts/${accountId}/expenses`),
  addExpense: (accountId, payload) => api.post(`/shared-accounts/${accountId}/expenses`, payload),
  updateExpense: (accountId, expenseId, payload) => api.put(`/shared-accounts/${accountId}/expenses/${expenseId}`, payload),
  // keepPlanned (cancelling from the budget): the expense goes back to planned.
  deleteExpense: (accountId, expenseId, keepPlanned = false) => api.delete(`/shared-accounts/${accountId}/expenses/${expenseId}`, keepPlanned ? { params: { keep_planned: true } } : undefined),
  toggleReaction: (accountId, expenseId, emoji) => api.post(`/shared-accounts/${accountId}/expenses/${expenseId}/reactions`, { emoji }),
  getBalances: (accountId) => api.get(`/shared-accounts/${accountId}/balances`),
  sendRecap: (accountId, payload) => api.post(`/shared-accounts/${accountId}/send-recap`, payload),
};

export const shareApi = {
  list: () => api.get('/share'),
  create: (d) => api.post('/share', d),
  update: (id, d) => api.patch(`/share/${id}`, d),
  remove: (id) => api.delete(`/share/${id}`),
  checkSlug: (slug) => api.get('/share/slug/check', { params: { slug } }),
  // Password in a header, not the address: addresses end up in browser
  // history and server logs.
  getPublic: (slug, password) => api.get(`/share/public/${slug}`, password ? { headers: { 'X-Profile-Password': password } } : undefined),
  trackClick: (slug, link_id) => api.post(`/share/public/${slug}/click`, null, { params: { link_id } }),
  setPassword: (id, password) => api.put(`/share/${id}/password`, { password: password || null }),
  analytics: (id, days = 30) => api.get(`/share/${id}/analytics`, { params: { days } }),
};

export const billingApi = {
  getPlans: () => api.get('/billing/plans'),
  checkout: (plan_id, origin_url, promo_code = null) => api.post('/billing/checkout', { plan_id, origin_url, promo_code }),
  getStatus: (session_id) => api.get(`/billing/status/${session_id}`),
  cancel: () => api.post('/billing/cancel'),
  refundEligibility: () => api.get('/billing/refund-eligibility'),
  requestRefund: () => api.post('/billing/request-refund'),
};

export const playlistApi = {
  fetchMetadata: (url) => api.post('/widgets/playlist/metadata', { url }),
};

export const savingsApi = {
  listGoals: (status = 'active') => api.get(`/savings/goals?status=${status}`),
  limits: () => api.get('/savings/limits'),
  createGoal: (payload) => api.post('/savings/goals', payload),
  updateGoal: (id, patch) => api.patch(`/savings/goals/${id}`, patch),
  deleteGoal: (id) => api.delete(`/savings/goals/${id}`),
  archiveGoal: (id) => api.post(`/savings/goals/${id}/archive`),
  addDeposit: (id, payload) => api.post(`/savings/goals/${id}/deposits`, payload),
  listDeposits: (id) => api.get(`/savings/goals/${id}/deposits`),
  projection: (id) => api.get(`/savings/goals/${id}/projection`),
  extractProduct: (url) => api.post('/savings/extract-product', { url }),
  wishlistItems: () => api.get('/savings/wishlist-items'),
};

export const parkingApi = {
  getActive: () => api.get('/parking/sessions/active'),
  create: (payload) => api.post('/parking/sessions', payload),
  terminate: (id) => api.post(`/parking/sessions/${id}/terminate`),
  prolong: (id, addMinutes) => api.post(`/parking/sessions/${id}/prolong`, null, { params: { add_minutes: addMinutes } }),
  history: () => api.get('/parking/sessions/history'),
  createShareLink: (sessionId, validity = 'until_end') => api.post(`/parking/sessions/${sessionId}/share-links`, { validity }),
  listShareLinks: () => api.get('/parking/share-links'),
  revokeShareLink: (id) => api.delete(`/parking/share-links/${id}`),
  shareLimits: () => api.get('/parking/share-limits'),
  publicShare: (token) => api.get(`/parking/public/${token}`),
};

export default api;
